<template>
  <div class="contact-container">
    <div class="contact-grid">
      <div class="contact-info">
        <h2 class="section-title">Global Engineering Support</h2>

        <div class="info-card factory-panel">
          <div class="info-item">
            <Icon fontName="email" class="icon-container" />
            <div>
              <h3>Global Inquiries</h3>
              <p>
                <span style="font-size: 1.1rem; font-weight: 700"
                  >info@yoleecorp.com</span
                ><br />
                Direct connection to our project engineers.
              </p>
            </div>
          </div>

          <div class="info-item">
            <Icon fontName="tel" class="icon-container" />
            <div>
              <h3>International Hotline</h3>
              <p>
                +86 158-0399-0204<br />
                Mon – Fri, 9:00 AM – 5:00 PM<br />
                (GMT+8) English/Mandarin Support
              </p>
            </div>
          </div>

          <h3 class="panel-title">Our Machinery Brand</h3>

          <div class="factory-block">
            <div class="factory-header">
              <Icon fontName="factory" class="factory-icon" />
              <div>
                <h4 class="factory-name">SHIXIAO MACHINERY(ABR®)</h4>
                <p class="factory-type">High-Precision Hardware Platform</p>
              </div>
            </div>
            <p class="factory-address">
              Manufacturing Base:<br />
              Huawei Road, Baizhai Industrial Zone<br />
              Zhengzhou, Henan, China (452300)
            </p>
          </div>

          <h3 class="panel-title">Global Sales & Service</h3>

          <div class="factory-block">
            <div class="factory-header">
              <Icon fontName="factory" class="factory-icon" />
              <div>
                <h4 class="factory-name">YOLEE® TECHNOLOGY</h4>
                <p class="factory-type">
                  Professional Global Service & Sales Gateway
                </p>
              </div>
            </div>
            <p class="factory-address">
              International Business Center:<br />
              No. 133 Yaozhai Road, Jinshui District<br />
              Zhengzhou, Henan, China (450000)
            </p>
          </div>
        </div>
      </div>

      <div class="form-wrapper">
        <h2 class="section-title">Request a Consultation</h2>

        <Form @submit-success="showSuccess" @submit-error="showError" />

        <transition name="fade">
          <div v-if="showSuccessMessage" class="feedback success">
            <Icon fontName="success" class="icon-container" />
            Request sent successfully. An engineer will contact you shortly.
          </div>
        </transition>

        <transition name="fade">
          <div v-if="showErrorMessage" class="feedback error">
            <Icon fontName="error" class="icon-container" />
            Please verify your information and try again.
          </div>
        </transition>
      </div>
    </div>

    <div class="yolee-bridge" ref="bridge">
      <div class="bridge-header">
        <Icon fontName="bridge" class="bridge-icon" />
        <h3 class="bridge-title">
          <span class="yolee">YOLEE®</span> — Empowering the ABR® Platform
        </h3>
      </div>

      <div class="bridge-content">
        <div class="bridge-section">
          <h4>The Professional Gateway</h4>
          <p>
            Launched in 2019, <strong>YOLEE®</strong> serves as the specialized
            professional service and sales organization for
            <strong>ABR®</strong> machinery. YOLEE® provides the essential
            "content"—handling technical communication, international
            compliance, and global project management.
          </p>
        </div>

        <div class="bridge-section">
          <h4>Engineering-Led Support</h4>
          <p>
            Our mission is to bridge global manufacturers with high-performance
            ABR® production platforms. All technical solutions and engineering
            customizations at YOLEE® are personally overseen by our founder, Mr.
            Wu Xinyue. By integrating the factory's core technical expertise
            directly into our service platform, YOLEE® ensures absolute
            technical accuracy, rapid response, and a level of procurement
            transparency that transcends traditional manufacturing.
          </p>
        </div>

        <div class="bridge-section">
          <h4>Partnership Over Competition</h4>
          <p>
            We believe in building long-term industrial partnerships through
            quality and process knowledge rather than price-driven competition.
            YOLEE® prioritizes the stability of your production and the
            long-term competitiveness of your brand in the global market.
          </p>
        </div>
      </div>
    </div>

    <p class="privacy-notice">
      By submitting this form, you agree to our
      <router-link to="/policy">Privacy Policy</router-link>. Your technical
      data is handled with strict industrial confidentiality.
    </p>
  </div>
</template>

<script>
import Form from "@/components/Form";
import Icon from "@/components/Icon";

export default {
  components: { Form, Icon },
  data() {
    return {
      showSuccessMessage: false,
      showErrorMessage: false,
    };
  },
  mounted() {
    this.$emit("set-product-page", true);
    this.handleScroll();
    window.addEventListener("scroll", this.handleScroll);
  },
  beforeDestroy() {
    this.$emit("set-product-page", false);
    window.removeEventListener("scroll", this.handleScroll);
  },
  methods: {
    showSuccess() {
      this.showSuccessMessage = true;
      this.showErrorMessage = false;
      setTimeout(() => (this.showSuccessMessage = false), 5000);
    },
    showError() {
      this.showErrorMessage = true;
      this.showSuccessMessage = false;
    },
    handleScroll() {
      const el = this.$refs.bridge;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const windowHeight =
        window.innerHeight || document.documentElement.clientHeight;

      if (rect.top < windowHeight * 0.85) {
        el.classList.add("in-view");
        el.querySelector(".bridge-content").classList.add("in-view");
      }
    },
  },
};
</script>

<style lang="less" scoped>
@import "~@/assets/styles/variables.less";
@import "~@/assets/styles/mixins.less";

.contact-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
}

/* ========== GRID 布局 ========== */
.contact-grid {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 3rem;
  margin-bottom: 2rem;

  @media (max-width: @breakpoint-tablet) {
    grid-template-columns: 1fr;
  }
}

.section-title {
  color: @color-text-title;
  font: 600 28px/1.2 @font-en;
  margin-bottom: 2rem;
  border-bottom: 2px solid @color-primary;
  padding-bottom: 0.5rem;
}

/* ========== 信息卡片 ========== */
.info-card {
  background: @color-bg-light;
  .cardRadius();
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 6px 18px fade(@color-primary, 10%);
}

/* 工厂信息面板 */
.factory-panel {
  background: #f3f4f5;
  border: 1px solid fade(@color-text-title, 10%);
  border-radius: 2px; /* 统一去圆角风格 */
  padding: 2.2rem;
}

.panel-title {
  font: 600 16px @font-en;
  margin-bottom: 2rem;
  color: @color-text-title;
  letter-spacing: 1px;
  text-transform: uppercase;

  &:before {
    content: "";
    width: 40px;
    height: 3px;
    background: @color-primary;
    display: block;
    margin-bottom: 0.7rem;
    border-radius: 0;
  }
}

/* 每个品牌 block */
.factory-block {
  background: white;
  padding: 1.2rem;
  border-radius: 2px;
  box-shadow: 0 4px 12px fade(@color-primary, 8%);
  margin-bottom: 1.6rem;
  border-left: 4px solid @color-primary;
}

.factory-header {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.factory-icon {
  font-size: 2rem;
  color: @color-primary;
  flex-shrink: 0;
}

.factory-name {
  font: 700 17px/1.3 @font-en;
  color: @color-text-title;
  letter-spacing: 0.5px;
}

.factory-type {
  font-size: 13px;
  color: @color-primary;
  font-weight: 600;
  margin-top: 2px;
}

.factory-address {
  margin-top: 0.5rem;
  color: @color-text-sub;
  line-height: 1.6;
  font-size: 14px;
}

/* ========== 联系信息项 ========== */
.info-item {
  display: flex;
  gap: 1.2rem;
  margin-bottom: 2rem;

  .icon-container {
    font-size: 2rem;
    color: @color-primary;
    flex-shrink: 0;
  }

  h3 {
    color: @color-primary;
    font: 600 18px/1.3 @font-en;
    margin-bottom: 0.4rem;
  }

  p {
    color: @color-text-content;
    line-height: 1.6;
    font-size: 14.5px;
  }
}

/* ========== 表单区域 ========== */
.form-wrapper {
  position: relative;
  .cardRadius();
}

/* 反馈消息 */
.feedback {
  .flex-center();
  gap: 0.8rem;
  padding: 1rem;
  margin-top: 1.5rem;
  border-radius: 4px;
  font-weight: 500;

  &.success {
    background: fade(@color-status-success, 12%);
    color: @color-status-success;
  }

  &.error {
    background: fade(@color-status-error, 12%);
    color: @color-status-error;
  }

  .icon-container {
    font-size: 1.2rem;
  }
}

/* ========== YOLEE 介绍 ========== */
.yolee-bridge {
  background: #f8faf9;
  padding: 3.5rem 2.5rem;
  border-radius: 2px;
  margin-bottom: 3rem;
  border: 1px solid #edf1ef;
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 1s ease, transform 1s ease;

  &.in-view {
    opacity: 1;
    transform: translateY(0);
  }
}

.bridge-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
}

.bridge-icon {
  font-size: 3rem;
  color: @color-primary;
  flex-shrink: 0;
}

.bridge-title {
  font: 700 28px @font-en;
  color: @color-text-title;

  .yolee {
    color: @color-primary;
    font-weight: 800;
  }
}

.bridge-content {
  color: @color-text-content;
  font-size: 16px;
  line-height: 1.8;

  .bridge-section {
    margin-bottom: 2.5rem;

    h4 {
      font-size: 18px;
      font-weight: 700;
      color: @color-primary;
      margin-bottom: 0.8rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    p {
      margin: 0;
      color: @color-text-sub;
    }
  }
}

/* ========== 隐私声明 ========== */
.privacy-notice {
  text-align: center;
  color: @color-text-sub;
  font-size: 13px;
  margin-top: 2rem;

  a {
    color: @color-primary;
    text-decoration: underline;
  }
}

/* ========== Mobile 优化 ========== */
@media (max-width: 768px) {
  .contact-container {
    padding: 1rem;
  }

  .bridge-icon {
    display: none;
  }

  .bridge-title {
    font-size: 22px;
  }

  .info-card,
  .form-wrapper {
    padding: 1.5rem;
  }

  .bridge-content p {
    font-size: 15px;
  }
}
</style>
